import React, { useState } from 'react';
import { useAuth } from '../../contexts/AuthContext';
import { Link } from 'react-router-dom';
import { EnvelopeIcon, CheckCircleIcon, BuildingStorefrontIcon, UserIcon } from '@heroicons/react/24/outline';

export const EmailLinkSignIn = () => {
  const [email, setEmail] = useState('');
  const [loading, setLoading] = useState(false);
  const [sent, setSent] = useState(false);
  const [error, setError] = useState('');
  const { sendEmailLink } = useAuth();

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError('');

    try {
      await sendEmailLink(email);
      setSent(true);
    } catch (error) {
      console.error('Error sending email link:', error);
      setError(error.message || 'Failed to send sign-in link');
    } finally {
      setLoading(false);
    }
  };

  if (sent) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-pink-50 to-orange-50 py-12 px-4 sm:px-6 lg:px-8">
        <div className="max-w-md w-full space-y-8">
          <div className="text-center">
            <CheckCircleIcon className="mx-auto h-24 w-24 text-green-500" />
            <h2 className="mt-6 text-3xl font-extrabold text-gray-900">
              Check Your Email
            </h2>
            <p className="mt-2 text-sm text-gray-600">
              We've sent a sign-in link to <span className="font-medium text-pink-600">{email}</span>
            </p>
            <div className="mt-6 p-4 bg-blue-50 rounded-lg">
              <p className="text-sm text-blue-800">
                Click the link in your email to sign in. The link will expire in 1 hour.
              </p>
            </div>
            <div className="mt-4">
              <button
                onClick={() => {
                  setSent(false);
                  setEmail('');
                }}
                className="text-pink-600 hover:text-pink-500 text-sm font-medium"
              >
                Send to a different email
              </button>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-pink-50 to-orange-50 py-12 px-4 sm:px-6 lg:px-8">
      <div className="max-w-md w-full space-y-8">
        <div>
          <div className="mx-auto h-24 w-24 bg-gradient-to-r from-pink-500 to-orange-400 rounded-full flex items-center justify-center">
            <EnvelopeIcon className="h-12 w-12 text-white" />
          </div>
          <h2 className="mt-6 text-center text-3xl font-extrabold text-gray-900">
            Sign in to PFI Connect
          </h2>
          <p className="mt-2 text-center text-sm text-gray-600">
            Enter your email to receive a secure sign-in link
          </p>
        </div>
        <form className="mt-8 space-y-6" onSubmit={handleSubmit}>
          <div>
            <label htmlFor="email" className="sr-only">
              Email address
            </label>
            <input
              id="email"
              name="email"
              type="email"
              autoComplete="email"
              required
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              className="relative block w-full px-3 py-3 border border-gray-300 placeholder-gray-500 text-gray-900 rounded-lg focus:outline-none focus:ring-2 focus:ring-pink-500 focus:border-pink-500 focus:z-10 sm:text-sm"
              placeholder="Enter your email address"
            />
          </div>

          {error && (
            <div className="rounded-md bg-red-50 p-4">
              <div className="text-sm text-red-700">{error}</div>
            </div>
          )}

          <div>
            <button
              type="submit"
              disabled={loading || !email}
              className="group relative w-full flex justify-center py-3 px-4 border border-transparent text-sm font-medium rounded-lg text-white bg-gradient-to-r from-pink-500 to-orange-400 hover:from-pink-600 hover:to-orange-500 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-pink-500 disabled:opacity-50 disabled:cursor-not-allowed transition-all duration-200"
            >
              {loading ? (
                <div className="flex items-center">
                  <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
                  Sending link...
                </div>
              ) : (
                'Send sign-in link'
              )}
            </button>
          </div>

          <div className="text-center">
            <p className="text-xs text-gray-500">
              By signing in, you agree to Pink Flamingo Interiors' terms of service
            </p>
          </div>
        </form>
        
        {/* Divider */}
        <div className="relative mt-8">
          <div className="absolute inset-0 flex items-center" aria-hidden="true">
            <div className="w-full border-t border-gray-300"></div>
          </div>
          <div className="relative flex justify-center text-sm">
            <span className="px-4 bg-gradient-to-br from-pink-50 to-orange-50 text-gray-500">or</span>
          </div>
        </div>
        
        {/* Supplier Portal Access */}
        <div className="mt-8 bg-white rounded-xl shadow-lg border border-gray-200 p-6">
          <div className="flex items-center mb-4">
            <BuildingStorefrontIcon className="h-6 w-6 text-blue-600 mr-3" />
            <h3 className="text-lg font-semibold text-gray-900">Supplier Portal</h3>
          </div>
          <p className="text-sm text-gray-600 mb-4">
            Are you a supplier? Access your quotes and job sheets through our dedicated supplier portal.
          </p>
          <Link
            to="/supplier-login"
            className="w-full flex items-center justify-center px-4 py-3 border border-blue-600 text-blue-600 rounded-lg hover:bg-blue-50 transition-colors font-medium"
          >
            <UserIcon className="h-5 w-5 mr-2" />
            Supplier Portal Access
          </Link>
          <p className="text-xs text-gray-500 mt-3 text-center">
            You'll need your registered email and phone number to access the supplier portal
          </p>
        </div>
      </div>
    </div>
  );
};